/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type {
  OnTimeComponentViewportChange,
  TimeComponentZoomParameters
} from '../timeComponent.types';
import { PX_FACTOR_PER_LINE } from '../hooks/useWheel';
import { getISOString } from './timeUtils';

const DEFAULT_ZOOM_IN_FACTOR = 1.25;
const DEFAULT_ZOOM_OUT_FACTOR = 1 / 1.25;

/**
 * Gets the zoom viewport given the current viewport, zoom location, zoom factor, etc.
 * @param time The zoom location.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param zoomFactor The zoom factor.
 * @param minViewportDuration The min viewport duration.
 * @param maxViewportDuration The max viewport duration.
 * @returns The new viewport if the zoom were to happen.
 */
const getZoomViewport = (
  time: number,
  viewportStartTime: number,
  viewportEndTime: number,
  startTime: number,
  endTime: number,
  zoomFactor: number,
  minViewportDuration: number,
  maxViewportDuration: number
) => {
  // Let
  // t = time point that triggered the zoom
  // vs = viewport start time
  // ve = viewport end time
  // s = start time
  // e = end time
  // N = zoom factor, e.g. zoom factor == 2 means the content should "stretch"/"scale" by 2x.
  //
  // Zooming by N means:
  //     current viewport: (vs, ve)
  //     current viewport duration: (ve - vs)
  //     final viewport: (vs + ds, ve - de) for some ds, de
  //     final viewport duration: (1/N)*(ve - vs) = (ve - de) - (vs + ds)
  //
  // Solving for ds and de:
  // (ve - de) - (vs + ds) = (1/N)*(ve - vs)
  // (ve - vs) - (de + ds) = (1/N)*(ve - vs)
  // (de + ds) = (ve - vs) * (1 - 1/N)
  //
  // Note that (t - vs) / (ve - vs) = ds / (de + ds). Rearranging and substituting the above:
  // ds = (de + ds) * (t - vs) / (ve - vs)
  // ds = (ve - vs) * (1 - 1/N) * (t - vs) / (ve - vs)
  // ds = (1 - 1/N) * (t - vs)
  //
  // de = (ve - vs) * (1 - 1/N) - ds
  // de = (ve - vs) * (1 - 1/N) - (1 - 1/N) * (t - vs)
  // de = (1 - 1/N)(ve - vs - t + vs)
  // de = (1 - 1/N)(ve - t)
  //
  // Therefore, assuming NO bounds and constraints:
  // ds = (1 - 1/N) * (t - vs)
  // de = (1 - 1/N)(ve - t)
  // Final viewport after zoom: (vs + ds, ve - de)
  //
  // In reality, there is a max amount of zoom in (minViewportDuration),
  // and a max amount of zoom out (maxViewportDuration),
  // and the final viewport is constrained by the start and end edges,
  // so the code below takes these constraints into account:

  const t = time;
  const vs = viewportStartTime;
  const ve = viewportEndTime;
  const s = startTime;
  const e = endTime;
  const N = zoomFactor;

  const dur = ve - vs;
  const dsMax = Math.max(0, dur - minViewportDuration) * ((t - vs) / dur);
  const deMax = Math.max(0, dur - minViewportDuration) * ((ve - t) / dur);
  const dsMin = -Math.max(0, maxViewportDuration - dur) * ((t - vs) / dur);
  const deMin = -Math.max(0, maxViewportDuration - dur) * ((ve - t) / dur);

  const ds = Math.max(dsMin, Math.min(dsMax, (1 - 1 / N) * (t - vs)));
  const de = Math.max(deMin, Math.min(deMax, (1 - 1 / N) * (ve - t)));

  const sOverShoot = Math.max(0, s - (vs + ds));
  const eOverShoot = Math.max(0, ve - de - e);

  return {
    newViewportStartTime: Math.max(s, vs + (ds - eOverShoot)),
    newViewportEndTime: Math.min(e, ve - (de - sOverShoot))
  };
};

/**
 * Gets the best zoom order index given a viewport.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param zoomOrderViewDurationThresholds The computed zoom order viewport duration thresholds.
 * @returns
 */
const getZoomOrderIndex = (
  viewportStartTime: number,
  viewportEndTime: number,
  zoomOrderViewDurationThresholds: number[]
) => {
  const duration = viewportEndTime - viewportStartTime;
  const nextIndex = [...zoomOrderViewDurationThresholds, -Infinity].findIndex(
    (threshold) => duration > threshold
  );
  return nextIndex - 1;
};

/**
 * Gets the best time axes scales given the viewport.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param zoomOrderViewDurationThresholds The computed zoom order viewport duration thresholds.
 * @param minorAxisZoomOrder The minor axis zoom order.
 * @param minorAxisZoomOrderIndex The current minor axis zoom order index.
 * @param majorAxisZoomOrder The major axis zoom order.
 * @param majorAxisZoomOrderIndex The current major axis zoom order index.
 * @returns The best time axes scales given the viewport.
 */
const getZoomScales = (
  viewportStartTime: number,
  viewportEndTime: number,
  zoomOrderViewDurationThresholds: number[],
  minorAxisZoomOrder: TimeComponentZoomParameters['minorAxisZoomOrder'],
  minorAxisZoomOrderIndex: number,
  majorAxisZoomOrder?: TimeComponentZoomParameters['majorAxisZoomOrder'],
  majorAxisZoomOrderIndex?: number
) => {
  const idealMinorZoomOrderIndex = getZoomOrderIndex(
    viewportStartTime,
    viewportEndTime,
    zoomOrderViewDurationThresholds
  );
  let indexOffset = 0;
  if (majorAxisZoomOrder === undefined) {
    indexOffset = idealMinorZoomOrderIndex - minorAxisZoomOrderIndex;
  } else if (idealMinorZoomOrderIndex >= minorAxisZoomOrderIndex) {
    indexOffset = Math.min(
      idealMinorZoomOrderIndex - minorAxisZoomOrderIndex,
      majorAxisZoomOrder.length - 1 - majorAxisZoomOrderIndex!
    );
  } else {
    indexOffset = -Math.min(
      minorAxisZoomOrderIndex - idealMinorZoomOrderIndex,
      majorAxisZoomOrderIndex!
    );
  }
  return {
    minorAxisScale: minorAxisZoomOrder[minorAxisZoomOrderIndex + indexOffset],
    majorAxisScale: majorAxisZoomOrder && majorAxisZoomOrder[majorAxisZoomOrderIndex! + indexOffset]
  };
};

/**
 * Gets the equivalent zoom factor from a zoom gesture value (e.g. wheel delta value).
 * @param delta The zoom gesture value.
 * @returns The corresponding zoom factor to apply.
 */
const getGestureZoomFactor = (delta: number) => {
  const deltaVal = Math.abs(delta);
  const deltaSign = Math.sign(delta);
  // Formula is adopted from TimeComponent Toolkit
  // But in toolkit we use the deprecated WheelEvent.wheelDelta(Y) instead of WheelEvent.delta(Y)
  // We use the latter here, but the two values are a bit different and it's unclear how they relate.
  // According to MDN wheelDelta is platform dependent but generally represents mouse wheel turns,
  // and in our case delta represent pixels.
  // So the constants in the formula below are tweaked based on "feel"
  // (e.g., changed from 0.02 toolkit value to 0.05, and approximating wheelDelta as delta/(px per line)).
  return ((deltaVal / PX_FACTOR_PER_LINE) * 0.05 + 1) ** deltaSign;
};

/**
 * Performs a zoom, by invoking a viewport change callback with the new viewport.
 * @param time The zoom location.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param startTime The start time.
 * @param endTime The end time.
 * @param zoomFactor The zoom factor.
 * @param minZoomViewportDuration The min viewport duration.
 * @param maxZoomViewportDuration The max viewport duration.
 * @param zoomOrderViewDurationThresholds The computed zoom order viewport duration thresholds.
 * @param minorAxisZoomOrder The major axis zoom order.
 * @param minorAxisZoomOrderIndex The current minor axis zoom order index.
 * @param majorAxisZoomOrder The major axis zoom order.
 * @param majorAxisZoomOrderIndex The current major axis zoom order index.
 * @param onViewportChange The viewport change callback.
 * @returns The new viewport, and whether the viewport actually changed.
 */
const zoomTime = (
  time: number,
  viewportStartTime: number,
  viewportEndTime: number,
  startTime: number,
  endTime: number,
  zoomFactor: number,
  minZoomViewportDuration: number,
  maxZoomViewportDuration: number,
  zoomOrderViewDurationThresholds: number[],
  minorAxisZoomOrder: TimeComponentZoomParameters['minorAxisZoomOrder'],
  minorAxisZoomOrderIndex: number,
  majorAxisZoomOrder?: TimeComponentZoomParameters['majorAxisZoomOrder'],
  majorAxisZoomOrderIndex?: number,
  onViewportChange?: OnTimeComponentViewportChange
) => {
  const { newViewportStartTime, newViewportEndTime } = getZoomViewport(
    time,
    viewportStartTime,
    viewportEndTime,
    startTime,
    endTime,
    zoomFactor,
    minZoomViewportDuration,
    maxZoomViewportDuration
  );
  const { minorAxisScale, majorAxisScale } = getZoomScales(
    newViewportStartTime,
    newViewportEndTime,
    zoomOrderViewDurationThresholds,
    minorAxisZoomOrder,
    minorAxisZoomOrderIndex,
    majorAxisZoomOrder,
    majorAxisZoomOrderIndex
  );
  const newViewport = {
    viewportStartTime: newViewportStartTime,
    viewportEndTime: newViewportEndTime,
    majorAxisScale,
    minorAxisScale
  };
  const isViewportChanged =
    newViewportStartTime !== viewportStartTime || newViewportEndTime !== viewportEndTime;

  if (onViewportChange && isViewportChanged) {
    onViewportChange({
      viewportStart: getISOString(newViewportStartTime),
      viewportEnd: getISOString(newViewportEndTime),
      majorAxisScale,
      minorAxisScale
    });
  }

  return { newViewport, isViewportChanged };
};

export { zoomTime, getGestureZoomFactor, DEFAULT_ZOOM_IN_FACTOR, DEFAULT_ZOOM_OUT_FACTOR };
